import React, { useCallback, useLayoutEffect } from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { useResponsive } from '@garnish/core';

import { LocationSearch, TransparentModal, useHeaderButtonOptions } from '../../../../components';
import {
  useCancelReorder,
  useDismissInactiveReorderRoute,
  useNavigationActions,
  useReorderState,
} from '../../hooks';
import {
  useReorderLeftIcon,
  useReorderRightIcon,
} from '../ReorderConfirmLocation/ReorderConfirmLocation.components';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderChangeLocation = () => {
  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,
    deliveryOrderDetail,
  } = useReorderState();
  const { formatMessage } = useIntl();

  const activeReorderRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const activeReorderDeliveryAddressId = isReordering ? deliveryOrderDetail?.addressId : undefined;

  const { currentStep } = useReorderState();
  const cancelReorder = useCancelReorder();
  const { currentBreakpoint } = useResponsive();

  useDismissInactiveReorderRoute();

  const { onBack, onClose } = useNavigationActions();
  const leftIcon = useReorderLeftIcon();
  const rightIcon = useReorderRightIcon();

  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  const renderHeaderLeft = useCallback(() => leftIcon, [leftIcon]);
  const renderHeaderRight = useCallback(() => rightIcon, [rightIcon]);

  const headerLeft = onBack && currentBreakpoint.isXS ? renderHeaderLeft : undefined;
  const headerRight = onClose && currentBreakpoint.isXS ? renderHeaderRight : undefined;

  const { headerLeftOptions, headerRightOptions } = useHeaderButtonOptions({
    headerLeft,
    headerRight,
  });

  const shouldRenderLocationSearch =
    currentStep === 'locationSearch' || currentStep === 'confirmingGroupOrderReorder';

  useLayoutEffect(() => {
    setOptions({
      ...headerLeftOptions,
      ...headerRightOptions,
      headerShown: false,
    });
  }, [headerLeftOptions, headerRightOptions, setOptions]);

  if (!shouldRenderLocationSearch && Platform.OS === 'web') return null;

  return (
    <TransparentModal
      testID="reorder-change-location"
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      disableAnimation={currentBreakpoint.isXS}
      size="large"
      offset="10%"
      onClose={cancelReorder}
    >
      <LocationSearch
        isInsideModal
        interactedLocationRestaurantSlug={activeReorderRestaurantSlug}
        interactedLocationDeliveryAddressId={activeReorderDeliveryAddressId}
      />
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Select a location',
    description: 'Reorder | Change location | Dialog a11y label',
  },
});
